import { RawAxiosRequestConfig } from 'axios';
import { PublisherUpdatesApi, Update, PagedUpdatesResponse } from 'cmp-client-api';

import { suppressErrorToastHeader } from '@src/server/suppress-error-toast';

import {
  PublisherContractsPageableRequestConfig,
  PublisherUpdatesPageableRequestConfig,
  PublishersRequestConfig,
  PublisherUpdatesServiceInterface,
} from '@src/server/services/types/publisher-updates-service.interface';
import { MapperInterface } from '@src/server/converters/types/mapper.interface';
import {
  PageablePublisherContracts,
  PageablePublisherUpdates,
  Publisher,
  PublisherUpdate,
} from '@src/domain/pages/publisher-updates/table/types';
import { PagedContractsResponse } from 'cmp-client-api/dist/api';

// Keep a safety margin below common ~2KB URL limits (proxies/gateways) using the *encoded* query-string length.
const MAX_SAFE_PUBLISHER_UPDATES_QUERY_LENGTH = 1800;
const appendArrayParam = (params: URLSearchParams, key: string, values?: string[]) => {
  if (values?.length) {
    params.set(key, values.join('|'));
  }
};

const appendScalarParam = (params: URLSearchParams, key: string, value?: string | number) => {
  if (value !== undefined) {
    params.set(key, String(value));
  }
};

const buildPublisherUpdatesQueryLength = ({
  search,
  regions,
  publishers,
  types,
  sortField,
  sortOrder,
  pageSize,
  page,
}: PublisherUpdatesPageableRequestConfig): number => {
  const params = new URLSearchParams();

  appendScalarParam(params, 'search', search);
  appendArrayParam(params, 'regions', regions);
  appendArrayParam(params, 'publishers', publishers);
  appendArrayParam(params, 'types', types);
  appendScalarParam(params, 'sortField', sortField);
  appendScalarParam(params, 'sortOrder', sortOrder);
  appendScalarParam(params, 'pageSize', pageSize);
  appendScalarParam(params, 'page', page);

  return params.toString().length;
};

export class PublisherUpdatesService implements PublisherUpdatesServiceInterface {
  private readonly service: PublisherUpdatesApi;

  private readonly mapper: MapperInterface<PublisherUpdate[], Update[]>;

  private readonly pageableMapper: MapperInterface<PageablePublisherUpdates, PagedUpdatesResponse>;

  private readonly pageableContractsMapper: MapperInterface<
    PageablePublisherContracts,
    PagedContractsResponse
  >;

  private readonly availablePublisherTypes = [
    'General',
    'Contract',
    'Promotion',
    'Product',
    'Pricing',
    'Licensing',
  ];

  public constructor(
    service: PublisherUpdatesApi,
    mapper: MapperInterface<PublisherUpdate[], Update[]>,
    pageableMapper: MapperInterface<PageablePublisherUpdates, PagedUpdatesResponse>,
    pageableContractsMapper: MapperInterface<PageablePublisherContracts, PagedContractsResponse>
  ) {
    this.service = service;
    this.mapper = mapper;
    this.pageableMapper = pageableMapper;
    this.pageableContractsMapper = pageableContractsMapper;
  }

  public async getAllPublishers(config?: PublishersRequestConfig): Promise<Publisher[]> {
    // The SCU is resolved from the auth token on the backend, so only the flag is sent.
    // suppressErrorToast is translated into a standard request header, which the global
    // interceptor (see cmp-http-client-hook) reads to skip its error toast for this request.
    const options: RawAxiosRequestConfig = {
      ...(config?.contractsOnly ? { params: { contractsOnly: 'true' } } : {}),
      ...(config?.suppressErrorToast ? { headers: suppressErrorToastHeader() } : {}),
    };

    return this.service
      .getAllPublishers(options)
      .then(({ data }) => data)
      .then((data) => data.sort((a, b) => a.name.localeCompare(b.name)));
  }

  public async getAllRegions(): Promise<string[]> {
    return this.service.getAllRegions().then(({ data }) => data);
  }

  public async getAllTypes(): Promise<string[]> {
    return this.availablePublisherTypes;
  }

  public async getPageablePublisherUpdates(
    config: PublisherUpdatesPageableRequestConfig
  ): Promise<PageablePublisherUpdates> {
    const { page, pageSize, sortField, sortOrder, search, regions, publishers, types } = config;

    const request =
      buildPublisherUpdatesQueryLength(config) >= MAX_SAFE_PUBLISHER_UPDATES_QUERY_LENGTH
        ? this.service.postUpdatesPaginated(config)
        : this.service.getUpdatesPaginated(
            search,
            regions,
            publishers,
            types,
            sortField,
            sortOrder,
            pageSize,
            page
          );

    return request
      .then(({ data }) => data)
      .then((data) => this.pageableMapper.transformFromApiModel(data));
  }

  public async getPublisherUpdates(): Promise<PublisherUpdate[]> {
    return this.service
      .getUpdates()
      .then(({ data }) => data)
      .then(({ data }) => this.mapper.transformFromApiModel(data));
  }

  public async getPublisherUpdate(updateId: string): Promise<PublisherUpdate> {
    return this.service.getUpdate(updateId).then(({ data }) => {
      const [mappedUpdate] = this.mapper.transformFromApiModel([data]);
      return mappedUpdate;
    });
  }

  public async getContracts(
    config: PublisherContractsPageableRequestConfig
  ): Promise<PageablePublisherContracts> {
    const { search, scu, publisherId, page, pageSize, sortField, sortOrder } = config;

    return this.service
      .getUpdatesContracts(scu, publisherId, search, sortField, sortOrder, page, pageSize)
      .then(({ data }) => this.pageableContractsMapper.transformFromApiModel(data));
  }
}
